/**
 * Visão geral — a primeira tela depois do login.
 *
 * Regra única: **nenhum número fixo no código**. A versão anterior mostrava
 * "3.821 escolas" e "333 municípios" escritos à mão, de antes do Censo real —
 * que mede 2.740. Um painel que abre com número inventado ensina o operador a
 * desconfiar de todos os outros.
 *
 * A tela responde, nesta ordem, as três perguntas de quem chega de manhã:
 *   1. Quanto mercado existe e quanto já virou dinheiro?
 *   2. O que está andando agora (pedidos, campanhas, quem respondeu)?
 *   3. O que está bloqueado e qual é o próximo passo?
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

import { api, type Categoria, type EstadoEleitoral, type Lista, type TabelaPreco } from '../api/cliente'
import {
  Aviso,
  Botao,
  Carregando,
  Cartao,
  Etiqueta,
  formatarDataCurta,
} from '../componentes/ui'
import { NOMES_PAPEL, podeAdministrar, podeGerirSistema, useAuth } from '../estado/auth'

const brl = (c: number): string =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const num = (n: number): string => n.toLocaleString('pt-BR')

interface ResumoMercado {
  ano: number
  escolasNoUniverso: number
  resumo: {
    escolas: number
    concluintes: number
    valorCentavos: number
    escolasPara80Pct: number
    porSituacao: Record<string, { escolas: number; valorCentavos: number }>
  }
  realizado: { valorCentavos: number; pedidos: number }
}

interface ResumoLeads {
  total: number
  porOrigem: Record<string, number>
}

interface CampanhaLinha {
  id: string
  nome: string
  status: string
  classeComunicacao: string
}

interface PedidoLinha {
  id: string
  protocolo: string
  status: string
}

const ROTULO_STATUS_PEDIDO: Record<string, string> = {
  rascunho: 'Rascunho',
  em_validacao: 'Em validação',
  aguardando_sec: 'Aguardando SEC',
  em_composicao: 'Em composição',
  aguardando_aprovacao: 'Aguardando aprovação',
  aguardando_pagamento: 'Aguardando pagamento',
  na_fila_publicacao: 'Na fila de publicação',
  publicado: 'Publicado',
  cancelado: 'Cancelado',
  rejeitado: 'Rejeitado',
}

function CartaoIndicador({
  para,
  rotulo,
  valor,
  detalhe,
  tom,
}: {
  para: string
  rotulo: string
  valor: string
  detalhe: ReactNode
  tom?: 'ok' | 'atencao'
}) {
  return (
    <Link
      to={para}
      className="cartao block p-5 transition hover:border-marca-300 hover:shadow-sm"
    >
      <p className="text-sm text-slate-500">{rotulo}</p>
      <p
        className={`mt-1 text-3xl font-semibold tabular ${
          tom === 'ok' ? 'text-emerald-700' : tom === 'atencao' ? 'text-amber-700' : 'text-slate-900'
        }`}
      >
        {valor}
      </p>
      <p className="mt-1 text-xs text-slate-400">{detalhe}</p>
    </Link>
  )
}

export function Painel() {
  const { usuario } = useAuth()
  const admin = podeAdministrar(usuario?.papel)
  // Só Configurações fica atrás da gestão do sistema — o restante a equipe
  // EGBA vê por inteiro (o RBAC dá leitura de categoria e preço ao papel).
  const gestao = podeGerirSistema(usuario?.papel)

  const { data: mercado } = useQuery({
    queryKey: ['mercado-resumo'],
    queryFn: () => api.get<{ dados: ResumoMercado }>('/api/v1/mercado/resumo'),
    enabled: admin,
    retry: false,
  })

  const { data: leads } = useQuery({
    queryKey: ['empresas-resumo'],
    queryFn: () => api.get<{ dados: ResumoLeads }>('/api/v1/leads/resumo'),
    enabled: admin,
    retry: false,
  })

  const { data: campanhas } = useQuery({
    queryKey: ['campanhas'],
    queryFn: () => api.get<Lista<CampanhaLinha>>('/api/v1/campanhas'),
    enabled: admin,
    retry: false,
  })

  // Contagem por status feita no cliente porque hoje a fila tem poucos
  // pedidos; quando crescer, isto vira uma rota de resumo — este comentário é
  // o lembrete.
  const { data: pedidos } = useQuery({
    queryKey: ['pedidos-painel'],
    queryFn: () => api.get<Lista<PedidoLinha>>('/api/v1/pedidos?porPagina=100'),
    retry: false,
  })

  const { data: categorias, isLoading } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
    enabled: admin,
  })

  const { data: precos } = useQuery({
    queryKey: ['precos'],
    queryFn: () => api.get<Lista<TabelaPreco>>('/api/v1/admin/precos'),
    enabled: admin,
  })

  // Compliance eleitoral é leitura da operação (classe de peça, janela de
  // vedação) — para o cliente isso é ruído jurídico que não muda nada no
  // pedido dele, então nem a consulta roda.
  const { data: eleitoral } = useQuery({
    queryKey: ['eleitoral'],
    queryFn: () => api.get<EstadoEleitoral>('/compliance/eleitoral'),
    enabled: admin,
  })

  const hoje = new Date().toISOString().slice(0, 10)
  const ativas = (categorias?.dados ?? []).filter((c) => c.ativa)
  const vigentes = (precos?.dados ?? []).filter(
    (t) => t.ativa && t.vigenciaInicio <= hoje && (!t.vigenciaFim || t.vigenciaFim >= hoje),
  )
  const semTabela = ativas.filter((c) => !vigentes.some((t) => t.categoriaId === c.id))

  const m = mercado?.dados
  const emAndamento = (pedidos?.dados ?? []).filter(
    (p) => !['publicado', 'cancelado', 'rejeitado', 'rascunho'].includes(p.status),
  )
  const porStatus = emAndamento.reduce<Record<string, number>>((acc, p) => {
    acc[p.status] = (acc[p.status] ?? 0) + 1
    return acc
  }, {})
  const campanhasAtivas = (campanhas?.dados ?? []).filter((c) => c.status === 'ativa')
  const campanhasRascunho = (campanhas?.dados ?? []).filter((c) => c.status === 'rascunho')
  const formularios = leads?.dados.porOrigem?.landing_page ?? 0

  if (admin && isLoading) return <Carregando />

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Olá, {usuario?.nome?.split(' ')[0]}</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          {NOMES_PAPEL[usuario?.papel ?? ''] ?? usuario?.papel}
        </p>
      </div>

      {/* Pendência real: sem tabela vigente o pedido não avança para pagamento. */}
      {admin && semTabela.length > 0 && (
        <Aviso tom="atencao" titulo="Cobrança bloqueada por falta de tabela de preço">
          {semTabela.length === 1 ? 'A categoria ' : 'As categorias '}
          <strong>{semTabela.map((c) => c.nome).join(', ')}</strong>
          {semTabela.length === 1 ? ' não tem' : ' não têm'} tabela vigente. Enquanto isso, o pedido
          não avança para pagamento.{' '}
          <Link to="/precos" className="font-medium">
            Cadastrar tabela
          </Link>
        </Aviso>
      )}

      {/* ------------------------------------------- visão geral do cliente */}
      {!admin && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <CartaoIndicador
              para="/pedidos"
              rotulo="Aguardando sua aprovação"
              valor={String((pedidos?.dados ?? []).filter((p) => p.status === 'aguardando_aprovacao').length)}
              tom={
                (pedidos?.dados ?? []).some((p) => p.status === 'aguardando_aprovacao')
                  ? 'atencao'
                  : undefined
              }
              detalhe="a EGBA mediu e o valor espera o seu de acordo"
            />
            <CartaoIndicador
              para="/pedidos"
              rotulo="Em andamento"
              valor={String(emAndamento.length)}
              detalhe={
                emAndamento.length === 0
                  ? 'nenhum pedido na fila'
                  : Object.entries(porStatus)
                      .map(([s, n]) => `${n} ${ROTULO_STATUS_PEDIDO[s]?.toLowerCase() ?? s}`)
                      .join(' · ')
              }
            />
            <CartaoIndicador
              para="/pedidos"
              rotulo="Rascunhos"
              valor={String((pedidos?.dados ?? []).filter((p) => p.status === 'rascunho').length)}
              detalhe="continue de onde parou"
            />
            <CartaoIndicador
              para="/pedidos"
              rotulo="Publicados"
              valor={String((pedidos?.dados ?? []).filter((p) => p.status === 'publicado').length)}
              tom={(pedidos?.dados ?? []).some((p) => p.status === 'publicado') ? 'ok' : undefined}
              detalhe="com certidão disponível na ficha"
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Cartao
              titulo="Publicar no Diário Oficial"
              descricao="Do balancete à página publicada, sem sair daqui."
              acoes={
                <Link to="/pedidos/novo">
                  <Botao>Nova publicação</Botao>
                </Link>
              }
            >
              <ol className="space-y-3">
                {[
                  ['1', 'Monte a matéria', 'Envie o XML do balancete, cole o texto para a IA estruturar ou digite — com prévia diagramada em centímetros reais.'],
                  ['2', 'A EGBA valida e mede', 'A equipe confere o conteúdo e mede a composição — o valor sai da medição oficial, pela tabela vigente.'],
                  ['3', 'Você aprova e paga', 'O valor volta para o seu de acordo antes de qualquer cobrança.'],
                  ['4', 'Publicação e certidão', 'A matéria sai no Diário e a certidão fica disponível na ficha do pedido.'],
                ].map(([n, titulo, texto]) => (
                  <li key={n} className="flex gap-3">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-marca-50 text-[11px] font-bold text-marca-700">
                      {n}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{titulo}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{texto}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Cartao>

            <Cartao
              titulo="Fale com a EGBA"
              descricao="Dúvida sobre um pedido, prazo ou valor — a equipe de publicações responde."
            >
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">E-mail</span>
                  <a href="mailto:publicacoes@egba.ba.gov.br" className="font-medium text-marca-700 hover:underline">
                    publicacoes@egba.ba.gov.br
                  </a>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Previsão de publicação</span>
                  <span className="text-slate-700">informada na ficha de cada pedido</span>
                </div>
                <p className="border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500">
                  O conteúdo enviado fica congelado durante a análise — qualquer ajuste depois do
                  envio é feito pela EGBA ou devolvendo o pedido para rascunho.
                </p>
              </div>
            </Cartao>
          </div>
        </>
      )}

      {/* ------------------------------------------------------- indicadores */}
      {admin && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <CartaoIndicador
            para="/mercado"
            rotulo="Mercado endereçável"
            valor={m ? brl(m.resumo.valorCentavos) : '—'}
            detalhe={
              m
                ? `${num(m.resumo.escolas)} escolas com concluintes · Censo ${m.ano}`
                : 'importe o Censo em Mercado de escolas'
            }
          />
          <CartaoIndicador
            para="/mercado"
            rotulo="Já publicado (realizado)"
            valor={m ? brl(m.realizado.valorCentavos) : '—'}
            tom={m && m.realizado.valorCentavos > 0 ? 'ok' : undefined}
            detalhe={
              m
                ? m.realizado.pedidos === 0
                  ? 'nenhuma publicação concluída — o mercado está inteiro pela frente'
                  : `${num(m.realizado.pedidos)} publicação(ões) concluída(s)`
                : '—'
            }
          />
          <CartaoIndicador
            para="/empresas"
            rotulo="Empresas na base"
            valor={leads ? num(leads.dados.total) : '—'}
            detalhe={
              formularios > 0
                ? `${num(formularios)} vieram de formulário — contato quente`
                : 'importadas da Receita; nenhuma respondeu ainda'
            }
          />
          <CartaoIndicador
            para="/pedidos"
            rotulo="Pedidos em andamento"
            valor={String(emAndamento.length)}
            tom={emAndamento.length > 0 ? 'atencao' : undefined}
            detalhe={
              emAndamento.length === 0
                ? 'nenhum pedido esperando ação'
                : Object.entries(porStatus)
                    .map(([s, n]) => `${n} ${ROTULO_STATUS_PEDIDO[s]?.toLowerCase() ?? s}`)
                    .join(' · ')
            }
          />
        </div>
      )}

      {/* ------------------------------------------------------------ funil */}
      {admin && m && (
        <Cartao
          titulo="Funil de captação"
          descricao="Lead é quem agiu — formulário preenchido ou envio recebido. A base importada é universo."
          acoes={
            <Link to="/mercado">
              <Botao variante="secundario" tamanho="sm">
                Abrir mercado
              </Botao>
            </Link>
          }
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(
              [
                ['nao_trabalhada', 'Não trabalhada', 'neutro'],
                ['lead', 'Lead', 'atencao'],
                ['em_negociacao', 'Em negociação', 'marca'],
                ['cliente', 'Cliente', 'sucesso'],
              ] as const
            ).map(([chave, rotulo, tom]) => {
              const v = m.resumo.porSituacao[chave] ?? { escolas: 0, valorCentavos: 0 }
              return (
                <div key={chave} className="rounded-xl border border-[color:var(--color-borda)] p-4">
                  <Etiqueta tom={tom}>{rotulo}</Etiqueta>
                  <p className="mt-2 text-2xl font-semibold text-slate-900 tabular">
                    {num(v.escolas)}
                  </p>
                  <p className="text-xs text-slate-500">{brl(v.valorCentavos)}</p>
                </div>
              )
            })}
          </div>
        </Cartao>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ------------------------------------------------ próximos passos */}
        {admin && (
          <Cartao
            titulo="Próximos passos da captação"
            descricao="O caminho até a primeira campanha no ar. Disparar é sempre manual."
          >
            <ol className="space-y-4">
              {[
                {
                  feito: campanhasRascunho.length > 0 || campanhasAtivas.length > 0,
                  titulo: 'Revisar a campanha modelo de cobrança',
                  texto:
                    'Régua pronta em três tons — informativo, firme e formal — todos citando a Res. CEE 44/2020, apontando para a landing page e para publicacoes@egba.ba.gov.br. Ajuste o texto e o público antes de ativar.',
                  para: '/campanhas',
                  acao: 'Abrir campanhas',
                },
                {
                  feito: false,
                  titulo: 'Importar as bases internas da EGBA',
                  texto:
                    'Sócios dos CNPJs das escolas, contatos do administrativo e do financeiro. Chegando com opt-in registrado, o passo de WhatsApp da régua passa a sair; sem opt-in, o dispatcher segura sozinho, contato a contato.',
                  para: '/empresas',
                  acao: 'Ver a base atual',
                },
                {
                  feito: formularios > 0,
                  titulo: 'Ligar primeiro para quem respondeu',
                  texto:
                    formularios > 0
                      ? `${num(formularios)} escola(s) preencheram o formulário da landing page — contato quente, na frente de qualquer lista fria.`
                      : 'Quem preencher o formulário das landing pages vira lead de verdade e aparece aqui.',
                  para: '/empresas',
                  acao: 'Abrir empresas',
                },
              ].map((p) => (
                <li key={p.titulo} className="flex gap-3">
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                      p.feito ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {p.feito ? '✓' : '•'}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{p.titulo}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{p.texto}</p>
                    <Link
                      to={p.para}
                      className="mt-1 inline-block text-xs font-medium text-marca-700 underline decoration-dotted underline-offset-2"
                    >
                      {p.acao}
                    </Link>
                  </div>
                </li>
              ))}
            </ol>
            {campanhasAtivas.length > 0 && (
              <p className="mt-4 border-t border-[color:var(--color-borda-suave)] pt-3 text-xs text-slate-500">
                {num(campanhasAtivas.length)} campanha(s) ativa(s) agora.
              </p>
            )}
          </Cartao>
        )}

        {/* --------------------------------------------------- eleitoral */}
        {admin && (
        <Cartao
          titulo="Compliance eleitoral"
          descricao="Lei 9.504/97, art. 73, VI, “b”"
          acoes={
            gestao ? (
              <Link to="/configuracoes">
                <Botao variante="secundario" tamanho="sm">
                  Configurar
                </Botao>
              </Link>
            ) : undefined
          }
        >
          {eleitoral ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Janela de vedação</span>
                <Etiqueta tom={eleitoral.janelaAtiva ? 'atencao' : 'sucesso'}>
                  {eleitoral.janelaAtiva ? 'Ativa' : 'Inativa'}
                </Etiqueta>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Período</span>
                <span className="text-slate-700 tabular">
                  {formatarDataCurta(eleitoral.janelaInicio)} a{' '}
                  {formatarDataCurta(eleitoral.janelaFim)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Modo</span>
                <span className="text-slate-700">{eleitoral.modo}</span>
              </div>
              {eleitoral.janelaAtiva && (
                <p className="border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500">
                  A cobrança da Res. 44/2020 é <strong>obrigacional</strong> e continua liberada na
                  janela — é aviso de dever legal, não publicidade. Peça institucional e
                  promocional fica bloqueada no envio.
                </p>
              )}
            </div>
          ) : (
            <Carregando texto="Consultando…" />
          )}
        </Cartao>
        )}
      </div>
    </div>
  )
}
